import React, { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import RefreshButton from '../../../components/fields/buttons/refresh-button';
import TableTab from "../../../components/tabs/table-tab";
import { REPORTS_ZONE_OCCUPANCY } from "../../../constants/menu-types";
import { useLocale } from "../../../contexts/localeContext";
import { TableContextProvider } from "../../../contexts/table/TableContextProvider";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import ZoneOccupanyColumns from "../../../models/ColumnDefinitions/ZoneOccupanyColumns";
import { ListData } from "../../../models/ListData";
import { ListQuery } from "../../../models/ListQuery";
import { Lookup } from "../../../models/Lookup";
import LookupTypes from "../../../models/LookupTypes";
import CompanySearch from "../../../models/search/CompanySearch";
import DepartmentSearch from "../../../models/search/DepartmentSearch";
import SiteSearch from "../../../models/search/SiteSearch";
import ZoneOccupancySearch from "../../../models/search/ZoneOccupancySearch";
import ZoneSearch from "../../../models/search/ZoneSearch";
import { auditRunReport } from "../../../services/auditService";
import { getCompanyNamesWithPagination } from "../../../services/companyService";
import { getDepartmentNamesWithPagination } from "../../../services/departmentService";
import { getZoneOccupancyReport } from "../../../services/reportService";
import { getSiteNamesWithPagination } from "../../../services/siteService";
import { getZoneNamesWithPagination } from "../../../services/zoneService";
import { IAppState } from "../../../store/types";

interface ZoneOccupancyReportProps {
    reportId: string;
}

const ZoneOccupancyReport: FC<ZoneOccupancyReportProps> = ({ reportId }) => {
    const { translate } = useLocale();
    const [lookup, setLookup] = useState<Lookup[]>([]);
    const [refreshReport, setRefreshReport] = useState(true);
    const user = useSelector((state: IAppState) => (state.userInfo));

    useEffect(() => {
        setLookup([
            {
                name: LookupTypes.department,
                values: [],
                dataMethod: getDepartmentNamesWithPagination,
                searchAccessor: DepartmentSearch.find(item => item.accessor === "name")?.searchOnName
            },
            {
                name: LookupTypes.site,
                values: [],
                dataMethod: getSiteNamesWithPagination,
                searchAccessor: SiteSearch.find(column => column.accessor === "name")?.searchOnName
            },
            {
                name: LookupTypes.zone,
                values: [],
                dataMethod: getZoneNamesWithPagination,
                searchAccessor: ZoneSearch.find(column => column.accessor === "name")?.searchOnName
            },
            {
                name: LookupTypes.company,
                values: [],
                dataMethod: getCompanyNamesWithPagination,
                searchAccessor: CompanySearch.find(item => item.accessor === "name")?.searchOnName
            },
        ]);
    }, [])

    useEffect(() => {
        if (refreshReport) {
            dataRefresh(getDataMethod)
        }
        setRefreshReport(false);
    }, [refreshReport])

    // default to a method that returns nothing
    // when putting a method in a state variable, useState requires us to return the method from a method, hence the wrapped inner method.
    const [dataRefresh, setDataRefresh] = useState(() => (dataMethod?: (query?: ListQuery) => Promise<ListData>) => { return });

    const manualDataRefresh = (refreshData: (dataMethod?: (query?: ListQuery) => Promise<ListData>) => void) => {
        setDataRefresh(() => refreshData);
    }
    // <-- end context

    // user preference items
    const userLoginId = useSelector((state: any) => (state.userInfo.id))
    const currentMenuDetail = REPORTS_ZONE_OCCUPANCY;
    const initialiseUserPreferences = (setDetails: (userLoginId, currentMenuDetail, [], reportId) => void) => {
        setDetails(userLoginId, currentMenuDetail, [], reportId);
    }
    // <-- end context

    const getDataMethod = async (listParameters?: ListQuery) => {
        try {
            await auditRunReport(reportId);
            return getZoneOccupancyReport(user.profileId, listParameters);
        } catch (error) {
            return Promise.reject(error);
        }
    }

    return (<TableContextProvider
        dataMethod={getDataMethod}
        tableDescriptor={ZoneOccupanyColumns}
        searchDescriptor={ZoneOccupancySearch}
        initialiser={(setDetails) => initialiseUserPreferences(setDetails)}
        manualDataRefresh={manualDataRefresh}
        useFilter
        useSort
        usePagination
        useSearch
        useTable
        lookup={lookup}>
        <TableTab
            title={''}
            reportTitle={translate.byKey(TranslationKeys.zoneOccupancy)}
            selectRows={false}
            cyName="zoneOccupancyReport">
            <RefreshButton onRefresh={setRefreshReport} />
        </TableTab>
    </TableContextProvider >
    );
};

export default ZoneOccupancyReport;
